// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';

const sentryEnvironment = () => {
    if (location.hostname.includes('localhost')) return 'localhost';
    else if (process.env.NEXT_PUBLIC_SENTRY_ENV)
        return process.env.NEXT_PUBLIC_SENTRY_ENV;
    else if (location.hostname.includes('dstore')) return 'development';
    else if (location.hostname.includes('qstore')) return 'qa';
    else return 'production';
};

// Get release version with fallback hierarchy for Sentry v9
const getReleaseVersion = () => {
    // First priority: Explicit SENTRY_RELEASE from build
    if (process.env.NEXT_PUBLIC_SENTRY_RELEASE) {
        console.log('Using NEXT_PUBLIC_SENTRY_RELEASE:', process.env.NEXT_PUBLIC_SENTRY_RELEASE);
        return process.env.NEXT_PUBLIC_SENTRY_RELEASE;
    }
    
    // Second priority: GIT_TAG from build
    if (process.env.NEXT_PUBLIC_GIT_TAG) {
        console.log('Using NEXT_PUBLIC_GIT_TAG:', process.env.NEXT_PUBLIC_GIT_TAG);
        return process.env.NEXT_PUBLIC_GIT_TAG;
    }
    
    // Third priority: App version from package.json or build
    if (process.env.NEXT_PUBLIC_APP_VERSION) {
        console.log('Using NEXT_PUBLIC_APP_VERSION:', process.env.NEXT_PUBLIC_APP_VERSION);
        return process.env.NEXT_PUBLIC_APP_VERSION;
    }
    
    // Fourth priority: Build time version
    if (process.env.NEXT_PUBLIC_BUILD_TIME_VERSION) {
        console.log('Using NEXT_PUBLIC_BUILD_TIME_VERSION:', process.env.NEXT_PUBLIC_BUILD_TIME_VERSION);
        return process.env.NEXT_PUBLIC_BUILD_TIME_VERSION;
    }
    
    // Fifth priority: GitHub run number
    if (process.env.NEXT_PUBLIC_GITHUB_RUN_NUMBER) {
        const release = `build-${process.env.NEXT_PUBLIC_GITHUB_RUN_NUMBER}`;
        console.log('Using GitHub run number:', release);
        return release;
    }
    
    // Sixth priority: Git commit hash
    if (process.env.NEXT_PUBLIC_GIT_COMMIT) {
        const release = `commit-${process.env.NEXT_PUBLIC_GIT_COMMIT}`;
        console.log('Using git commit:', release);
        return release;
    }
    
    // Fallback: timestamp
    const timestamp = new Date().toISOString().replace(/[^0-9]/g, '').slice(0, 14);
    const release = `client-${timestamp}`;
    console.log('Using timestamp fallback:', release);
    return release;
};

const isProd = sentryEnvironment().includes('prod');
const releaseVersion = getReleaseVersion();

console.log('Sentry environment:', sentryEnvironment());
console.log('Sentry release version:', releaseVersion);

Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    
    // Set the release version explicitly
    release: releaseVersion,
    
    // Set environment
    environment: sentryEnvironment(),
    
    // Make sure errors aren't filtered out
    ignoreErrors: [
        'Failed to load Stripe.js',
        // Keep these common network errors that aren't actionable
        'A Network Error Has Occurred. Please Try Again',
        'Failed to fetch',
        'Network Error',
        'Loading chunk failed',
        'ChunkLoadError',
        'Loading CSS chunk failed',
        'Non-Error promise rejection captured',
        'AbortError'
    ],
    
    denyUrls: ['medusa/store/customers/me'],
    
    // Always report console errors
    // Default sampling rates will apply for transaction data
    tracesSampler: (samplingContext) => {
        // Always trace errors
        const hasError = samplingContext.transaction?.status === 'error' || 
                        samplingContext.transaction?.status === 'failure' ||
                        samplingContext.parentSampled;
        
        if (hasError) {
            return 1.0; // 100% sampling for errors
        }
        
        // API endpoints get higher sampling
        if (samplingContext.request?.url) {
            const url = samplingContext.request.url;
            
            if (url.includes('/api/') || 
                url.includes('fiserv') || 
                url.includes('firstdata') ||
                url.includes('akimbo') ||
                url.includes('giftcardmarket')) {
                return isProd ? 0.05 : 0.1;
            }
        }
        
        // Default sampling rate
        return isProd ? 0.01 : 0.1;
    },

    debug: false,

    // Always capture replays when errors occur
    replaysOnErrorSampleRate: 1.0, 
    
    // Completely disable general session replays
    replaysSessionSampleRate: 0,
    
    // Include PII for better error context
    sendDefaultPii: true,
    
    // Sentry v9 integrations syntax
    integrations: [
        Sentry.httpClientIntegration(),
        
        // Capture ALL console errors
        Sentry.captureConsoleIntegration({ 
            levels: ['error', 'warn'] // Capture both errors and warnings
        }),
        
        // Add global error handler integration to catch unhandled errors
        Sentry.globalHandlersIntegration({
            onerror: true,
            onunhandledrejection: true
        }),
        
        // Session replay only during errors
        Sentry.replayIntegration({
            maskAllInputs: false,
            blockAllMedia: true,
            networkDetailAllowUrls: [
                'akimbo',
                'giftcardmarket',
                'fiserv',
                'firstdata',
                'api.stripe.com',
            ],
        }),
    ],
    
    // Always process errors
    beforeSend: (event, hint) => {
        // Special handling for console errors
        if (hint?.originalException && 
            hint.originalException instanceof Error && 
            hint.originalException.message) {
            
            // Tag console errors
            if (hint.originalException.message.includes('Console error captured')) {
                event.tags = {
                    ...event.tags,
                    console_error: 'true'
                };
            }
            
            // Tag API errors
            if (event.request?.url) {
                const url = event.request.url;
                if (url.includes('/api/') ||
                    url.includes('fiserv') || 
                    url.includes('firstdata') ||
                    url.includes('akimbo') ||
                    url.includes('giftcardmarket')) {
                    
                    event.tags = {
                        ...event.tags,
                        api_call: 'true'
                    };
                }
            }
        }
        
        return event;
    }
});

// Add a global error handler to catch any console.error calls
const originalConsoleError = console.error;
console.error = function(...args) {
    // Call the original console.error
    originalConsoleError.apply(console, args);
    
    // Report to Sentry if not already captured by the integration
    try {
        const errorMsg = args.map(arg => 
            typeof arg === 'string' ? arg : 
            (arg instanceof Error ? arg.message : JSON.stringify(arg))
        ).join(' ');
        
        // Create an Error object to get a stack trace
        const errorObj = new Error(`Console error captured: ${errorMsg}`);
        
        // Capture as exception - release is automatically included from init()
        Sentry.captureException(errorObj, {
            tags: {
                console_error_manual: 'true'
            }
        });
    } catch (e) {
        // Catch any errors in our error handling to avoid loops
    }
};

// Sentry v9 has onRouterTransitionStart
export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;